import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({ baseURL: `${BASE}/api` });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("cf_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // SaaS: carry the selected tenant on pre-login requests (branding/login). When a
  // token is present the backend prioritizes the token's tenant, so this is safe.
  const t = localStorage.getItem("cf_tenant");
  if (t) { try { const o = JSON.parse(t); if (o && o.slug) config.headers["X-Tenant"] = o.slug; } catch { /* ignore */ } }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response && err.response.status === 401) {
      localStorage.removeItem("cf_token");
      localStorage.removeItem("cf_user");
      if (window.location.pathname !== "/login") window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

export const API_BASE = BASE;

// Fetch an authenticated binary (image) and return an object URL for <img src>.
export async function fetchBlobUrl(path, params = {}) {
  const res = await api.get(path, { params, responseType: "blob" });
  return URL.createObjectURL(res.data);
}

// Robustly resolve a filename from the response's Content-Disposition header.
function _filename(res, fallback) {
  const cd = res?.headers?.["content-disposition"] || "";
  const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(cd);
  return (m && decodeURIComponent(m[1])) || fallback || "document.pdf";
}

// Open an authenticated PDF in a new tab for viewing / printing.
// Raw window.open(url) loses the Authorization header → "Could not validate
// credentials". We open a blank tab synchronously (keeps the user-gesture so the
// popup is allowed), fetch the file as a blob (auth header attached), then point
// the tab at the blob URL. Falls back to an anchor click if the popup was blocked.
export async function openDoc(path, params = {}) {
  const w = window.open("", "_blank");
  try {
    const res = await api.get(path, { params, responseType: "blob" });
    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    if (w && !w.closed) {
      w.location.href = url;
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    if (w && !w.closed) w.close();
    throw e;
  }
}

// Download an authenticated PDF to the user's device (Export PDF).
export async function downloadDoc(path, params = {}) {
  const res = await api.get(path, { params, responseType: "blob" });
  const blob = new Blob([res.data], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = _filename(res, path.split("/").filter(Boolean).slice(-2).join("_") + ".pdf");
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export default api;
